import React from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { Checkbox, ListItemText } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(() => ({
  select: {
    minWidth: 120,
    maxWidth: 190,
  },
}));

// Estética unificada de filtros (misma que el filtro "Etiqueta"/"Estado").
const filterSelectStyle = {
  fontSize: 11,
  height: 22,
  border: "1px solid rgba(0,0,0,0.18)",
  borderRadius: 10,
  padding: "0 4px",
  background: "transparent",
  opacity: 0.7,
};

// feat_301 — filtro multi por usuario asignado (solo admins, gateado por el
// setting showUserFilter desde TicketsManager).
// INVARIANTE: el filtro NUNCA queda vacío — si destildar dejaría 0 usuarios y
// "Sin asignar" apagado, el click se ignora en silencio.
// feat_344 — tilde "Todos" al tope, mismo patrón que el de TicketsQueueSelect.
// ⚠ Diferencia con colas: allí "Todos" es un flag propio (showAllTickets) que
// además viaja al backend como showAll y BLOQUEA el resto de los items. Acá no
// existe ese flag (feature solo-frontend) → "Todos" es la selección completa
// (todos los usuarios + "Sin asignar"), que filtra exactamente lo mismo, y los
// items siguen destildables uno por uno (destildar uno apaga solo el "Todos").
const TicketsUserSelect = ({
  users = [],
  selectedUserIds = [],
  includeUnassigned = false,
  onChangeUsers,
  onChangeUnassigned,
  onToggleAllUsers,
}) => {
  const classes = useStyles();

  if (!users || users.length === 0) return null;

  // Estado derivado (no hay flag): "Todos" está tildado cuando lo están todos
  // los usuarios Y "Sin asignar".
  const allSelected =
    includeUnassigned && users.every(u => selectedUserIds.includes(u.id));

  const buildLabel = () => {
    // Todos seleccionados (o ninguno) → placeholder: no hay recorte efectivo.
    // Misma regla que TicketsQueueSelect.
    if (allSelected || (selectedUserIds.length === 0 && !includeUnassigned)) {
      return i18n.t("ticketsList.filters.userPlaceholder");
    }
    if (selectedUserIds.length === 1 && !includeUnassigned) {
      const found = users.find(u => u.id === selectedUserIds[0]);
      return found ? found.name : "1";
    }
    if (selectedUserIds.length === 0 && includeUnassigned) {
      return i18n.t("ticketsList.filters.userUnassigned");
    }
    return i18n.t("ticketsList.filters.userCount", {
      count: selectedUserIds.length + (includeUnassigned ? 1 : 0),
    });
  };

  const handleClickItem = (e, action) => {
    e.stopPropagation();
    action();
  };

  const handleUserToggle = (userId) => {
    const newIds = selectedUserIds.includes(userId)
      ? selectedUserIds.filter(id => id !== userId)
      : [...selectedUserIds, userId];
    // Guardia: no dejar el filtro vacío (sería equivalente a "sin filtro" y
    // confundiría al admin que cree seguir filtrando).
    if (newIds.length === 0 && !includeUnassigned) return;
    onChangeUsers(newIds);
  };

  const handleUnassignedToggle = () => {
    const next = !includeUnassigned;
    if (!next && selectedUserIds.length === 0) return;
    onChangeUnassigned(next);
  };

  return (
    <FormControl size="small" className={classes.select}>
      <Select
        multiple
        value={[]}
        displayEmpty
        disableUnderline
        renderValue={() => (
          <span style={{ fontSize: 11 }}>{buildLabel()}</span>
        )}
        style={filterSelectStyle}
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          PaperProps: { style: { maxHeight: 320 } },
        }}
        inputProps={{ style: { padding: "0 4px", fontSize: 11 } }}
      >
        {/* Separador por borde en vez de <Divider>: MUI v4 clona TODO hijo del
            Select con props de opción (selected/role) → un Divider las volcaría
            al DOM y dispararía warnings de React. */}
        <MenuItem
          dense
          onClick={e => handleClickItem(e, onToggleAllUsers)}
          style={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}
        >
          <Checkbox size="small" checked={allSelected} color="primary" />
          <ListItemText
            primary={i18n.t("ticketsList.filters.all")}
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 600 } }}
          />
        </MenuItem>
        <MenuItem
          dense
          onClick={handleUnassignedToggle}
          style={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}
        >
          <Checkbox size="small" checked={includeUnassigned} />
          <ListItemText
            primary={i18n.t("ticketsList.filters.userUnassigned")}
            primaryTypographyProps={{ style: { fontSize: 13 } }}
          />
        </MenuItem>
        {users.map(user => (
          <MenuItem dense key={user.id} onClick={() => handleUserToggle(user.id)}>
            <Checkbox
              size="small"
              checked={selectedUserIds.includes(user.id)}
            />
            <ListItemText
              primary={user.name}
              primaryTypographyProps={{ style: { fontSize: 13 } }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default TicketsUserSelect;
